/**
 * useDevicesFilters - Hook para filtrar e ordenar dispositivos
 */

import { useState, useMemo, useCallback } from 'react';

const useDevicesFilters = (devices) => {
  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [searchText, setSearchText] = useState('');

  // Sort state
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filtered devices
  const filteredDevices = useMemo(() => {
    let result = [...devices];

    // Status filter
    if (statusFilter) {
      result = result.filter((d) => d.status === statusFilter);
    }

    // Group filter
    if (groupFilter !== 'all') {
      result = result.filter((d) => d.groupId === parseInt(groupFilter, 10));
    }

    // Search
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (d) => d.name?.toLowerCase().includes(lower)
          || d.uniqueId?.toLowerCase().includes(lower)
          || d.plate?.toLowerCase().includes(lower)
          || d.driverName?.toLowerCase().includes(lower)
          || d.groupName?.toLowerCase().includes(lower)
          || d.model?.toLowerCase().includes(lower),
      );
    }

    // Sorting
    result.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];

      // Handle special cases
      if (orderBy === 'lastUpdate') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }

      if (aVal === undefined || aVal === null) return 1;
      if (bVal === undefined || bVal === null) return -1;

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal?.toLowerCase() || '';
      }

      if (order === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return result;
  }, [devices, statusFilter, groupFilter, searchText, orderBy, order]);

  // Paginated devices
  const paginatedDevices = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredDevices.slice(start, start + rowsPerPage);
  }, [filteredDevices, page, rowsPerPage]);

  // Handlers
  const handleSort = useCallback((property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [orderBy, order]);

  const clearFilters = useCallback(() => {
    setStatusFilter('');
    setGroupFilter('all');
    setSearchText('');
    setPage(0);
  }, []);

  const hasActiveFilters = statusFilter || groupFilter !== 'all' || searchText.trim();

  return {
    // Filters
    statusFilter,
    setStatusFilter: (value) => { setStatusFilter(value); setPage(0); },
    groupFilter,
    setGroupFilter: (value) => { setGroupFilter(value); setPage(0); },
    searchText,
    setSearchText: (value) => { setSearchText(value); setPage(0); },

    // Sorting
    orderBy,
    order,
    handleSort,

    // Pagination
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage: (value) => { setRowsPerPage(value); setPage(0); },

    // Data
    filteredDevices,
    paginatedDevices,
    totalCount: filteredDevices.length,

    // Helpers
    hasActiveFilters,
    clearFilters,
  };
};

export default useDevicesFilters;
